import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { useApi } from "@/hooks/useApi";
import { useScrollToBottom } from "@/hooks/useScrollToBottom";
import { useSpeechInput } from "@/hooks/useSpeechInput";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CompanyDTO, ForcedSearchTool, SearchMode } from "@shaarp/common";
import { Loader2Icon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { RiRobot2Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { RotatingText } from "../ai-elements/rotating-text";
import { SpeechInput } from "../ai-elements/speech-input";
import AvatarCompany from "../company/avatar-company";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { ButtonSearchCompanies } from "./button-search-companies";
import { SearchToolSelector } from "./search-tool-selector";

export type InputCommandSearchCompaniesSelected = {
  text: string;
};

export default function InputSearchCompanies({
  selected,
  isLoading,
  isRelaunch = false,
  focus = false,
  forcedTool = null,
  searchMode = "strict",
  personalizedCapabilities = [],
  onForcedToolChange = () => {},
  onSearchModeChange = () => {},
  onLaunchNewSearch = () => {},
  onRelaunch = () => {},
  onChange = () => {},
  onCancel = () => {},
}: {
  selected?: InputCommandSearchCompaniesSelected;
  focus?: boolean;
  isLoading?: boolean;
  isRelaunch?: boolean;
  forcedTool?: ForcedSearchTool | null;
  searchMode?: SearchMode;
  personalizedCapabilities?: string[];
  onForcedToolChange?: (tool: ForcedSearchTool | null) => void;
  onSearchModeChange?: (mode: SearchMode) => void;
  onLaunchNewSearch?: (value: InputCommandSearchCompaniesSelected) => void;
  onRelaunch?: () => void;
  onChange?: (value: InputCommandSearchCompaniesSelected) => void;
  onCancel?: () => void;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    speechLanguage,
    handlePermissionDenied,
    handleError,
    handleConnectionLost,
  } = useSpeechInput();

  const commandInput = useRef<HTMLInputElement>(null);
  const commandList = useRef<HTMLDivElement>(null);

  const [input, setInput] = useState<string>("");
  const [showCommands, setShowCommands] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const [isLoadingCompaniesAvailable, setIsLoadingCompaniesAvailable] =
    useState<boolean>(false);
  const [suggestionCompaniesAvailable, setSuggestionCompaniesAvailable] =
    useState<CompanyDTO[]>([]);

  // Track the last search term to avoid clearing results when re-searching similar terms
  const lastSearchTermRef = useRef<string>("");

  // Keep a stable ref of the focus prop for use inside debounce callbacks
  const focusRef = useRef(focus);
  focusRef.current = focus;

  // Delay gradient spin to avoid one-frame square flash when loading starts (browser repaint)
  const [isSpinning, setIsSpinning] = useState(false);
  const spinDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isLoading) {
      spinDelayRef.current = setTimeout(() => setIsSpinning(true), 500);
    } else {
      if (spinDelayRef.current) {
        clearTimeout(spinDelayRef.current);
        spinDelayRef.current = null;
      }
      setIsSpinning(false);
    }
    return () => {
      if (spinDelayRef.current) clearTimeout(spinDelayRef.current);
    };
  }, [isLoading]);

  // Track whether the dropdown was previously showing results
  const wasDropdownOpenRef = useRef(false);

  // Compute if dropdown should be visible - show only if we have results,
  // or if loading started while the dropdown was already open
  const hasSuggestions = suggestionCompaniesAvailable.length > 0;
  const hasDropdownContent =
    input.length > 0 &&
    (hasSuggestions || (isLoadingCompaniesAvailable && wasDropdownOpenRef.current));
  const isDropdownVisible = showCommands && hasDropdownContent;

  // Keep ref in sync
  wasDropdownOpenRef.current = isDropdownVisible;

  // Delayed visibility: adds 200ms delay before starting close animation when input is cleared
  const [delayedIsDropdownVisible, setDelayedIsDropdownVisible] =
    useState(false);
  // Delayed unmount: keep content rendered during exit animation
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(false);
  const [cachedSuggestions, setCachedSuggestions] = useState<CompanyDTO[]>([]);

  // Cache suggestions whenever they change and dropdown is visible
  useEffect(() => {
    if (isDropdownVisible && suggestionCompaniesAvailable.length > 0) {
      setCachedSuggestions(suggestionCompaniesAvailable);
    }
  }, [isDropdownVisible, suggestionCompaniesAvailable]);

  useEffect(() => {
    if (isDropdownVisible) {
      // Immediately show
      setDelayedIsDropdownVisible(true);
      setShouldRenderDropdown(true);
    } else {
      // Add 200ms delay before starting the close animation
      const delayTimer = setTimeout(() => {
        setDelayedIsDropdownVisible(false);
      }, 200);

      // Delay unmount to allow full animation (200ms delay + 200ms animation)
      const unmountTimer = setTimeout(() => {
        setShouldRenderDropdown(false);
        // Clear suggestions only after dropdown is fully closed
        setSuggestionCompaniesAvailable([]);
      }, 400);

      return () => {
        clearTimeout(delayTimer);
        clearTimeout(unmountTimer);
      };
    }
  }, [isDropdownVisible]);

  // Use cached suggestions during exit animation, current ones otherwise
  // Use isDropdownVisible (immediate) not delayedIsDropdownVisible, because
  // suggestionCompaniesAvailable gets cleared immediately when input is empty
  const displayedSuggestions = isDropdownVisible
    ? suggestionCompaniesAvailable
    : cachedSuggestions;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        commandInput.current &&
        !commandInput.current.contains(event.target as Node) &&
        commandList.current &&
        !commandList.current.contains(event.target as Node)
      ) {
        setShowCommands(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selected && selected.text) {
      const trimmedValue = selected.text.trim();
      setInput(trimmedValue);
    } else {
      setInput("");
    }
  }, [selected]);

  useEffect(() => {
    if (focus) {
      commandInput.current?.focus();
      setTimeout(() => setShowCommands(true), 500);
    }
  }, [focus]);

  // Query captured when dictation starts. Live transcription rewrites the
  // field on every delta, so it is rebuilt from this snapshot instead of being
  // appended to, which would duplicate text as the transcript grows.
  const inputSnapshotRef = useRef("");

  const [isDictating, setIsDictating] = useState(false);
  // Keep the end of a dictated query visible in the single-line field.
  useScrollToBottom(commandInput, input, isDictating);

  const handleRecordingChange = useCallback(
    (isRecording: boolean) => {
      setIsDictating(isRecording);
      if (isRecording) {
        inputSnapshotRef.current = input;
      }
    },
    [input],
  );

  const handleTranscript = useCallback(
    (text: string) => {
      const snapshot = inputSnapshotRef.current;
      const newValue = snapshot.trim() ? `${snapshot.trimEnd()} ${text}` : text;
      setInput(newValue);
      setShowCommands(true);
      onChange({ text: newValue });
    },
    [onChange],
  );

  const handleInputChange = async (value: string) => {
    setInput(value);
    setShowCommands(true);
    onChange({
      text: value,
    });
  };

  useEffect(() => {
    // Don't fetch if input is empty, but don't clear suggestions immediately
    // (they'll be cleared after the close animation completes)
    if (input.length === 0) {
      lastSearchTermRef.current = "";
      return;
    }

    // Longer debounce to reduce flickering during rapid typing
    const timeoutId = setTimeout(() => {
      if (focusRef.current) {
        setShowCommands(true);
      }

      // Don't clear suggestions - keep showing old results while loading new ones
      lastSearchTermRef.current = input;
      fetchCompaniesAvailable();
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [input]);

  const selectCompany = (company: CompanyDTO) => {
    navigate(`/company/${company.id}`);
  };

  const isInputUnchanged = input.trim() === (selected?.text ?? "").trim();

  const launchSearch = () => {
    if (!input) {
      return;
    }

    setShowCommands(false);
    if (isRelaunch && isInputUnchanged) {
      onRelaunch();
    } else {
      onLaunchNewSearch({ text: input });
    }
  };

  const fetchCompaniesAvailable = async () => {
    setIsLoadingCompaniesAvailable(true);
    try {
      const { data } = await api.get<CompanyDTO[]>(`/companies/available`, {
        params: {
          search: input,
          limit: 5,
        },
      });
      setSuggestionCompaniesAvailable(data);
    } catch {
      // Silently fail — suggestions are non-critical
    } finally {
      setIsLoadingCompaniesAvailable(false);
    }
  };

  const staticCapabilities = t(
    "prospecting.inputSearchCompanies.capabilities",
    { returnObjects: true },
  ) as string[];

  const capabilitiesList =
    personalizedCapabilities.length > 0
      ? [...personalizedCapabilities, ...staticCapabilities]
      : staticCapabilities;

  return (
    <Command
      className="overflow-visible relative bg-transparent **:border-0 **:outline-hidden **:ring-0"
      shouldFilter={false}
    >
      <div className="flex justify-center">
        <div className="relative w-full max-w-4xl">
          <div className="relative w-full inline-flex overflow-hidden rounded-full p-[3px] focus-within:shadow-xl focus-within:shadow-purple-500/30 focus-within:-translate-y-0.5 transition-all duration-300">
            <span
              className={cn(
                "absolute inset-[-1000%] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]",
                isSpinning && "animate-[spin_2s_linear_infinite]",
              )}
            />
            <div className="flex relative items-center py-2 w-full bg-white rounded-full shadow-lg backdrop-blur-3xl transition-shadow duration-200 dark:bg-gray-900 hover:shadow-xl">
              <div className="flex-1 min-w-0">
                <div className="flex items-center">
                  <Avatar className="mr-3 ml-4 shrink-0 size-8">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      <RiRobot2Line />
                    </AvatarFallback>
                  </Avatar>

                  <div className="relative flex-1 min-w-0">
                    <CommandInput
                      classNameWrapper="px-0 border-0 flex-1 focus:outline-none focus:ring-0 focus:border-0"
                      asChild
                    >
                      <Input
                        ref={commandInput}
                        type="text"
                        placeholder=""
                        className="overflow-visible px-2 py-5 w-full text-gray-700 bg-transparent rounded-full border-0 disabled:opacity-100 dark:text-gray-200 md:text-lg focus:outline-none focus:ring-0 focus:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:border-0 active:outline-none active:ring-0 active:border-0"
                        style={{
                          border: "none",
                          outline: "none",
                          boxShadow: "none",
                        }}
                        value={input}
                        disabled={isLoading}
                        onFocus={() => {
                          setShowCommands(true);
                          setIsFocused(true);
                        }}
                        onBlur={() => {
                          setIsFocused(false);
                        }}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            launchSearch();
                          }
                        }}
                      />
                    </CommandInput>

                    {!isFocused && input === "" && (
                      <div className="absolute left-2 top-1/2 w-48 -translate-y-1/2 pointer-events-none md:w-auto">
                        <RotatingText
                          text={[...capabilitiesList].sort(
                            () => Math.random() - 0.5,
                          )}
                          className="text-gray-400 dark:text-gray-500 md:text-lg"
                          containerClassName="line-clamp-2"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex gap-1 items-center shrink-0">
                    {!isLoading && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <SpeechInput
                              variant="ghost"
                              size="icon"
                              className="rounded-full"
                              onTranscript={handleTranscript}
                              language={speechLanguage}
                              onPermissionDenied={handlePermissionDenied}
                              onError={handleError}
                              onConnectionLost={handleConnectionLost}
                              onRecordingChange={handleRecordingChange}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {t("chat.input.speech.tooltip")}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <SearchToolSelector
                      value={forcedTool}
                      onChange={onForcedToolChange}
                      searchMode={searchMode}
                      onSearchModeChange={onSearchModeChange}
                      disabled={isLoading}
                    />
                    <ButtonSearchCompanies
                      isLoading={isLoading}
                      isRelaunch={isRelaunch && isInputUnchanged}
                      hasInput={input.trim().length > 0}
                      onCancel={onCancel}
                      onRelaunch={launchSearch}
                      onSearch={launchSearch}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CommandList
        ref={commandList}
        className={cn(
          "absolute right-0 left-0 z-10 mx-auto max-w-4xl bg-white rounded-2xl shadow-2xl dark:bg-gray-900 max-h-[50vh] top-[70px]",
          "transition-all duration-200 ease-out",
          delayedIsDropdownVisible
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-2 pointer-events-none",
        )}
      >
        {shouldRenderDropdown && (
          <CommandGroup
            heading={
              <span className="inline-flex gap-2 items-center">
                {t("prospecting.inputSearchCompanies.groups.available")}
                {isLoadingCompaniesAvailable && (
                  <Loader2Icon className="w-3 h-3 animate-spin" />
                )}
              </span>
            }
            className="relative"
          >
            {/* Show previous results with loading indicator */}
            {displayedSuggestions.map((company) => (
              <CommandItem
                key={`available-${company.id}`}
                value={`available-${company.name}`}
                onSelect={() => selectCompany(company)}
                className={cn(
                  "gap-2 transition-opacity duration-150",
                  isLoadingCompaniesAvailable && "opacity-60",
                )}
              >
                <AvatarCompany className="w-6 h-6" company={company} />
                {company.name}
              </CommandItem>
            ))}
            {/* Show loading text only when we have no results yet */}
            {isLoadingCompaniesAvailable &&
              displayedSuggestions.length === 0 && (
                <CommandItem disabled={true}>
                  {t("prospecting.inputSearchCompanies.actions.loading")}
                </CommandItem>
              )}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}
